/**
 * Privacy Policy — approved copy supplied by Chrissa Valanti on 20 June 2026.
 * No changes other than the body content swap.
 */
import { useSEO, SEO } from "@/lib/seo";

const MAIL = "chrissavalanti@gmail.com";

function MailLink({ children }) {
    return (
        <a
            href={`mailto:${MAIL}`}
            className="text-oxblood underline-offset-4 hover:underline transition-colors"
        >
            {children || MAIL}
        </a>
    );
}

function H2({ n, children }) {
    return (
        <h2 className="font-serif text-2xl md:text-3xl leading-snug text-charcoal mt-16 mb-6">
            <span className="text-oxblood/70 mr-3">{n}.</span>
            {children}
        </h2>
    );
}

function P({ children }) {
    return <p className="body-base text-charcoal/80">{children}</p>;
}

function UL({ children }) {
    return (
        <ul className="body-base text-charcoal/80 list-disc pl-6 marker:text-oxblood/60 space-y-2">
            {children}
        </ul>
    );
}

export default function Privacy() {
    useSEO(SEO.privacy);
    return (
        <main className="bg-ivory text-charcoal">
            <section className="pt-36 md:pt-44 pb-10 md:pb-14">
                <div className="container-narrow">
                    <p className="eyebrow text-oxblood">Privacy</p>
                    <h1 className="display-2 mt-6">Privacy Policy.</h1>
                    <p className="mt-6 eyebrow text-stone">Effective date: 20 June 2026</p>
                </div>
            </section>

            <section className="pb-24 md:pb-32">
                <div className="container-narrow space-y-5">
                    <P>
                        This Privacy Policy explains how personal information is handled through the Chrissa
                        Valanti personal website available at:
                    </P>
                    <UL>
                        <li>chrissavalanti.com</li>
                        <li>chrissavalanti.com.au</li>
                    </UL>
                    <P>The website is operated by Chrissa Valanti.</P>
                    <P>
                        For privacy questions or requests, contact:
                    </P>
                    <P>
                        <strong className="text-charcoal font-medium">Email:</strong> <MailLink />
                    </P>

                    {/* 1 */}
                    <H2 n="1">Information collected</H2>
                    <P>The website may collect personal information when you:</P>
                    <UL>
                        <li>subscribe to receive updates;</li>
                        <li>submit a general enquiry;</li>
                        <li>make a media, podcast or speaking enquiry;</li>
                        <li>enquire about Valanti Labs, ALATI or music; or</li>
                        <li>contact me through another method.</li>
                    </UL>
                    <P>Depending on the form you use, the information collected may include:</P>
                    <UL>
                        <li>your name;</li>
                        <li>your email address;</li>
                        <li>your organisation;</li>
                        <li>the type of enquiry you are making;</li>
                        <li>the contents of your message;</li>
                        <li>your consent to the submission;</li>
                        <li>the page or section through which you subscribed; and</li>
                        <li>the date and time of your submission.</li>
                    </UL>
                    <P>
                        The website does not offer user accounts, accept payments, process bookings or permit
                        file uploads.
                    </P>

                    {/* 2 */}
                    <H2 n="2">Sensitive and health information</H2>
                    <P>
                        The website does not ask you to provide health information, diagnoses, treatment
                        details or other sensitive information.
                    </P>
                    <P>
                        Because the contact form includes a free-text message field, you may voluntarily
                        include personal or sensitive information in your message. Please provide only the
                        information that is reasonably necessary for your enquiry.
                    </P>
                    <P>
                        Personal stories and health experiences published on this website are shared for
                        general information and reflection. They are not medical advice and do not create a
                        doctor–patient, therapist–client or other professional relationship.
                    </P>

                    {/* 3 */}
                    <H2 n="3">Technical information</H2>
                    <P>
                        The website uses a temporary, in-memory rate-limiting system to protect its forms
                        from spam and misuse. Your internet protocol address may be read briefly for this
                        purpose. It is not intentionally saved in the website database and is ordinarily
                        removed from the temporary rate-limit memory after a short period or when the server
                        restarts.
                    </P>
                    <P>
                        The website hosting platform and server may also create technical logs containing
                        information such as:
                    </P>
                    <UL>
                        <li>your internet protocol address;</li>
                        <li>the date and time of access;</li>
                        <li>the page or request accessed;</li>
                        <li>the request method; and</li>
                        <li>the server response status.</li>
                    </UL>
                    <P>
                        The precise location and retention period of platform logs are controlled by the
                        hosting provider and may change.
                    </P>
                    <P>
                        The website does not intentionally collect device fingerprints, screen dimensions,
                        advertising identifiers or detailed behavioural profiles.
                    </P>

                    {/* 4 */}
                    <H2 n="4">How personal information is used</H2>
                    <P>Personal information may be used to:</P>
                    <UL>
                        <li>respond to your enquiry;</li>
                        <li>communicate with you about media, speaking, music or partnership opportunities;</li>
                        <li>communicate with you about Valanti Labs or ALATI;</li>
                        <li>maintain a record of enquiries and correspondence;</li>
                        <li>send updates that you have requested;</li>
                        <li>administer, secure and improve the website;</li>
                        <li>prevent spam, misuse or unlawful activity;</li>
                        <li>comply with legal obligations; and</li>
                        <li>protect legal rights and interests.</li>
                    </UL>
                    <P>
                        Information submitted for one purpose will not ordinarily be used for an unrelated
                        purpose without your consent unless permitted or required by law.
                    </P>

                    {/* 5 */}
                    <H2 n="5">Email subscriptions and communications</H2>
                    <P>
                        When you submit an email subscription form, your email address, and your first name
                        where provided, are stored in the website database.
                    </P>
                    <P>
                        The website does not currently operate an automated newsletter or marketing-email
                        system. Submitting the form records your interest in receiving future updates.
                    </P>
                    <P>
                        Before marketing or promotional emails are introduced, communications will include an
                        appropriate way to unsubscribe.
                    </P>
                    <P>
                        Until an automated unsubscribe method is available, you may ask for your subscription
                        record to be removed by emailing:
                    </P>
                    <P>
                        <MailLink />
                    </P>
                    <P>You may withdraw your consent at any time.</P>
                    <P>Submitting a contact enquiry does not automatically add you to a marketing list.</P>

                    {/* 6 */}
                    <H2 n="6">Storage and retention</H2>
                    <P>
                        Contact enquiries and subscription records are stored in a database associated with
                        the website&apos;s hosting environment.
                    </P>
                    <P>
                        The physical hosting location is currently controlled by the website platform
                        provider and has not been confirmed as being located in Australia.
                    </P>
                    <P>
                        There is currently no automatic deletion schedule. Records remain stored until they
                        are manually deleted or until the website&apos;s systems are changed.
                    </P>
                    <P>
                        You may request deletion of your information at any time. Information may be retained
                        where reasonably necessary to:
                    </P>
                    <UL>
                        <li>respond to an active enquiry;</li>
                        <li>maintain legitimate administrative or correspondence records;</li>
                        <li>resolve a dispute;</li>
                        <li>prevent misuse; or</li>
                        <li>comply with legal obligations.</li>
                    </UL>

                    {/* 7 */}
                    <H2 n="7">Overseas processing and external services</H2>
                    <P>
                        Some website infrastructure and content services may operate from, or process
                        technical information in, locations outside Australia.
                    </P>
                    <P>These services currently include:</P>
                    <UL>
                        <li>Emergent Labs, which provides website hosting, infrastructure and platform services;</li>
                        <li>Google Fonts, which provides website typefaces;</li>
                        <li>Unsplash, which provides certain editorial images;</li>
                        <li>Pexels, which provides certain editorial images; and</li>
                        <li>content-delivery networks used to deliver website assets.</li>
                    </UL>
                    <P>
                        When your browser connects to these services, the service may receive technical
                        information such as your internet protocol address, browser details and the page or
                        asset requested.
                    </P>
                    <P>
                        The countries in which these services process data may vary and are not always
                        practicable to identify.
                    </P>
                    <P>External providers handle information under their own terms and privacy practices.</P>

                    {/* 8 */}
                    <H2 n="8">Cookies and browser storage</H2>
                    <P>
                        The website application does not currently set its own cookies and does not
                        intentionally use local storage or session storage.
                    </P>
                    <P>
                        Third-party hosting or content services may use technical storage or processing that
                        is outside the direct control of this website.
                    </P>
                    <P>The website does not currently use:</P>
                    <UL>
                        <li>Google Analytics;</li>
                        <li>advertising pixels;</li>
                        <li>behavioural advertising;</li>
                        <li>retargeting;</li>
                        <li>heatmaps;</li>
                        <li>session recording; or</li>
                        <li>visitor profiling tools.</li>
                    </UL>
                    <P>
                        This Policy will be updated if analytics, cookies or similar technologies are
                        introduced.
                    </P>

                    {/* 9 */}
                    <H2 n="9">Disclosure of information</H2>
                    <P>Personal information may be disclosed where reasonably necessary to:</P>
                    <UL>
                        <li>website hosting and infrastructure providers;</li>
                        <li>technical service providers assisting with website operation;</li>
                        <li>professional advisers;</li>
                        <li>contractors assisting with an enquiry, where appropriate;</li>
                        <li>government authorities, regulators or law-enforcement bodies where required by law; or</li>
                        <li>another party where you have given consent.</li>
                    </UL>
                    <P>Personal information is not sold to advertisers or data brokers.</P>

                    {/* 10 */}
                    <H2 n="10">Security</H2>
                    <P>
                        Reasonable technical and administrative measures are used to protect information
                        handled through the website.
                    </P>
                    <P>Current protections include:</P>
                    <UL>
                        <li>encrypted HTTPS connections;</li>
                        <li>server-side input validation;</li>
                        <li>form consent requirements;</li>
                        <li>basic rate limiting;</li>
                        <li>anti-spam honeypot fields; and</li>
                        <li>database isolation within the hosting environment.</li>
                    </UL>
                    <P>
                        No method of online transmission or electronic storage is completely secure. Absolute
                        security cannot be guaranteed.
                    </P>
                    <P>Please do not submit highly sensitive information through the website contact form.</P>

                    {/* 11 */}
                    <H2 n="11">Access, correction and deletion</H2>
                    <P>You may request:</P>
                    <UL>
                        <li>access to personal information held about you;</li>
                        <li>correction of information that is inaccurate or outdated;</li>
                        <li>deletion of your information; or</li>
                        <li>withdrawal from future communications.</li>
                    </UL>
                    <P>Requests should be sent to:</P>
                    <P>
                        <MailLink />
                    </P>
                    <P>
                        To protect your privacy, reasonable information may be requested to confirm your
                        identity before acting on a request.
                    </P>
                    <P>
                        A request may be refused where permitted or required by law. Where appropriate, the
                        reason for refusal will be explained.
                    </P>

                    {/* 12 */}
                    <H2 n="12">Privacy concerns and complaints</H2>
                    <P>Privacy questions or complaints may be sent to:</P>
                    <P>
                        <strong className="text-charcoal font-medium">Chrissa Valanti</strong>
                        <br />
                        <strong className="text-charcoal font-medium">Email:</strong> <MailLink />
                    </P>
                    <P>Please include enough information to understand and investigate your concern.</P>
                    <P>Reasonable steps will be taken to review and respond to your complaint.</P>
                    <P>
                        Where applicable, you may also contact the Office of the Australian Information
                        Commissioner.
                    </P>

                    {/* 13 */}
                    <H2 n="13">Children</H2>
                    <P>
                        This website is not directed specifically at children and does not knowingly seek
                        personal information from children.
                    </P>
                    <P>
                        A parent or guardian who believes a child has submitted personal information may
                        contact me to request its removal.
                    </P>

                    {/* 14 */}
                    <H2 n="14">External links</H2>
                    <P>
                        The website may link to social-media platforms, music services, partner websites or
                        other third-party websites.
                    </P>
                    <P>
                        Those websites are independently operated and have their own terms and privacy
                        practices. This Privacy Policy does not govern information handled by those third
                        parties.
                    </P>

                    {/* 15 */}
                    <H2 n="15">Changes to this Policy</H2>
                    <P>
                        This Privacy Policy may be updated when the website, its services or its legal
                        obligations change.
                    </P>
                    <P>The current version will be published on this page with its effective date.</P>

                    {/* 16 */}
                    <H2 n="16">Contact</H2>
                    <P>
                        Questions about this Privacy Policy or the handling of personal information may be
                        directed to:
                    </P>
                    <P>
                        <strong className="text-charcoal font-medium">Chrissa Valanti</strong>
                        <br />
                        <strong className="text-charcoal font-medium">Email:</strong> <MailLink />
                    </P>
                </div>
            </section>
        </main>
    );
}
